import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import {
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import '../screens-monitoring.css';

interface ChannelState {
  at: string;
  ok: boolean;
  error: string | null;
}
interface AlertRow {
  id: number;
  rule: string;
  severity: 'critical' | 'warning' | 'info';
  status: 'open' | 'resolved';
  title: string;
  detail: string | null;
  openedAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  lastNotifiedAt: string | null;
  notified: boolean;
}
interface Rule {
  id: string;
  label: string;
  severity: string;
  meaning: string;
  action: string;
  thresholds: string[];
  external: boolean;
}
interface Overview {
  status: { configured: boolean; detail: string };
  channels: {
    webhook: { configured: boolean; format: string | null; last: ChannelState | null };
    email: { configured: boolean; recipients: string[]; last: ChannelState | null };
    heartbeat: {
      configured: boolean;
      hasFailUrl: boolean;
      last: {
        at: string;
        ok: boolean;
        sent: string;
        reasons: string[];
        error: string | null;
      } | null;
    };
    lastTest: { at: string; ok: boolean } | null;
    testFresh: boolean;
  };
  alerts: AlertRow[];
  silences: Array<{
    id: number;
    rule: string;
    reason: string;
    until: string;
    created_by: string | null;
  }>;
  thresholds: Array<{ key: string; value: unknown; default: unknown }>;
  rules: Rule[];
  probes: {
    smartemailing: { applicable: boolean; ok: boolean; checkedAt: string; failures: number } | null;
    backup: { newestAt: string | null; count: number } | null;
    disk: { freePercent: number } | null;
  };
  lastDigest: { at: string; ok: boolean } | null;
}

const SEVERITY: Record<string, { tone: Tone; text: string }> = {
  critical: { tone: 'bad', text: 'Kritické' },
  warning: { tone: 'warn', text: 'Varování' },
  info: { tone: 'neu', text: 'Informace' },
  warning_critical: { tone: 'warn', text: 'Varování / kritické' },
};

const FORMAT_TEXT: Record<string, string> = {
  slack: 'Slack',
  teams: 'Microsoft Teams',
  discord: 'Discord',
  json: 'obecný JSON',
};

interface Field2 {
  key: string;
  label: string;
  unit: string;
  kind: 'int' | 'pct' | 'bool';
}
const THRESHOLD_FIELDS: Field2[] = [
  {
    key: 'monitoring_tx_oldest_minutes',
    label: 'Nejstarší čekající transakční zpráva',
    unit: 'minut (kritické od čtyřnásobku)',
    kind: 'int',
  },
  {
    key: 'monitoring_tx_queue_size',
    label: 'Velikost fronty transakčních zpráv',
    unit: 'zpráv',
    kind: 'int',
  },
  {
    key: 'monitoring_tx_problem_count',
    label: 'Selhané a neznámé transakční zprávy za 24 h',
    unit: 'zpráv',
    kind: 'int',
  },
  {
    key: 'monitoring_dead_letter_count',
    label: 'Trvale selhané úlohy za 24 h',
    unit: 'úloh',
    kind: 'int',
  },
  {
    key: 'monitoring_tx_failure_rate',
    label: 'Podíl selhání jednoho kódu',
    unit: '%',
    kind: 'pct',
  },
  {
    key: 'monitoring_unconfirmed_after_minutes',
    label: 'Odesláno bez potvrzení doručení po',
    unit: 'minut (0 = vypnuto)',
    kind: 'int',
  },
  {
    key: 'monitoring_unconfirmed_min_count',
    label: 'Nejméně nepotvrzených zpráv',
    unit: 'zpráv',
    kind: 'int',
  },
  { key: 'monitoring_bounce_rate', label: 'Podíl odskoků za 24 h', unit: '%', kind: 'pct' },
  {
    key: 'monitoring_se_failures_before_alert',
    label: 'Selhání SmartEmailingu po sobě (kontrola po 5 minutách)',
    unit: 'kontrol',
    kind: 'int',
  },
  { key: 'worker_silent_minutes', label: 'Worker se neozval', unit: 'minut', kind: 'int' },
  {
    key: 'broadcast_stuck_minutes',
    label: 'Rozesílka se neposouvá déle než',
    unit: 'minut',
    kind: 'int',
  },
  {
    key: 'broadcast_error_threshold_percent',
    label: 'Rozesílka: práh odskoků a chyb',
    unit: '%',
    kind: 'int',
  },
  {
    key: 'broadcast_min_sample',
    label: 'Rozesílka: minimální vzorek pro práh',
    unit: 'zpráv',
    kind: 'int',
  },
  {
    key: 'monitoring_backup_max_age_hours',
    label: 'Nejstarší přípustná záloha',
    unit: 'hodin',
    kind: 'int',
  },
  {
    key: 'monitoring_disk_warn_percent',
    label: 'Volné místo na disku: varování pod',
    unit: '%',
    kind: 'int',
  },
  {
    key: 'monitoring_disk_critical_percent',
    label: 'Volné místo na disku: kritické pod',
    unit: '%',
    kind: 'int',
  },
  {
    key: 'monitoring_reminder_minutes',
    label: 'Připomínka kritického upozornění po',
    unit: 'minutách',
    kind: 'int',
  },
  {
    key: 'monitoring_cooldown_minutes',
    label: 'Znovuotevřené varování se neopakuje do',
    unit: 'minut',
    kind: 'int',
  },
  {
    key: 'monitoring_heartbeat_max_age_minutes',
    label: 'Nejstarší kontrola pro signál „jsem naživu“',
    unit: 'minut',
    kind: 'int',
  },
  {
    key: 'monitoring_digest_hour',
    label: 'Denní souhrn od hodiny (místní čas)',
    unit: 'hod',
    kind: 'int',
  },
  { key: 'monitoring_digest_enabled', label: 'Posílat denní souhrn', unit: '', kind: 'bool' },
];

function ChannelRow({
  name,
  configured,
  detail,
  last,
}: {
  name: string;
  configured: boolean;
  detail: string;
  last: ChannelState | null;
}): JSX.Element {
  return (
    <tr>
      <th scope="row">{name}</th>
      <td>
        {configured ? (
          <StatusBadge tone="ok">Nastaveno</StatusBadge>
        ) : (
          <StatusBadge tone="bad">Nenastaveno</StatusBadge>
        )}
      </td>
      <td>{detail}</td>
      <td>
        {last
          ? `${last.ok ? 'Naposledy v pořádku' : 'Naposledy selhalo'}: ${dateTime(last.at)}${last.error ? ` (${last.error})` : ''}`
          : configured
            ? 'Zatím nepoužito'
            : ''}
      </td>
    </tr>
  );
}

/** Nastavení → Dohled (vlna 0): kanály upozornění, aktivní a uzavřená upozornění, prahy, ztlumení. */
export function MonitoringTab({ canWrite }: { canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const q = useQuery<Overview>({
    queryKey: ['monitoring'],
    queryFn: () => api.get('/api/monitoring'),
    refetchInterval: 30_000,
  });
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [silRule, setSilRule] = useState('*');
  const [silMinutes, setSilMinutes] = useState(60);
  const [silReason, setSilReason] = useState('');
  const [edit, setEdit] = useState<Record<string, string | boolean>>({});

  const refresh = (): Promise<void> => qc.invalidateQueries({ queryKey: ['monitoring'] });
  const fail = (err: unknown): void =>
    setMessage({ ok: false, text: err instanceof Error ? err.message : 'Nepovedlo se.' });

  const test = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; results: Array<{ channel: string; ok: boolean; error?: string }> }>(
        '/api/monitoring/test',
      ),
    onSuccess: async (r) => {
      setMessage({
        ok: r.ok,
        text: r.ok
          ? 'Zkušební upozornění odešlo všemi kanály. Zkontrolujte, že dorazilo.'
          : `Některý kanál selhal: ${r.results
              .filter((x) => !x.ok)
              .map((x) => `${x.channel === 'email' ? 'e-mail' : 'webhook'}: ${x.error ?? 'chyba'}`)
              .join('; ')}`,
      });
      await refresh();
    },
    onError: fail,
  });
  const silence = useMutation({
    mutationFn: () =>
      api.post('/api/monitoring/silences', {
        rule: silRule,
        minutes: silMinutes,
        reason: silReason,
      }),
    onSuccess: async () => {
      setSilReason('');
      setMessage({
        ok: true,
        text: 'Ztlumení je zapnuté. Upozornění se po tu dobu jen zaznamenávají.',
      });
      await refresh();
    },
    onError: fail,
  });
  const unsilence = useMutation({
    mutationFn: (id: number) => api.del(`/api/monitoring/silences/${id}`),
    onSuccess: refresh,
    onError: fail,
  });
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      for (const f of THRESHOLD_FIELDS) {
        const v = edit[f.key];
        if (v === undefined) continue;
        body[f.key] = f.kind === 'bool' ? v : f.kind === 'pct' ? Number(v) / 100 : Number(v);
      }
      return api.put('/api/monitoring/thresholds', body);
    },
    onSuccess: async () => {
      setEdit({});
      setMessage({ ok: true, text: 'Prahy jsou uložené.' });
      await refresh();
    },
    onError: fail,
  });

  if (q.isLoading) return <LoadingState what="dohled" />;
  if (q.isError || !q.data) {
    return (
      <ErrorState error={q.error ?? new Error('Nelze načíst')} onRetry={() => void q.refetch()} />
    );
  }
  const d = q.data;
  const open = d.alerts.filter((a) => a.status === 'open');
  const closed = d.alerts.filter((a) => a.status === 'resolved');
  const valueOf = (f: Field2): string | boolean => {
    const edited = edit[f.key];
    if (edited !== undefined) return edited;
    const raw = d.thresholds.find((t) => t.key === f.key)?.value;
    if (f.kind === 'bool') return Boolean(raw);
    if (f.kind === 'pct') return String(Math.round(Number(raw) * 100));
    return String(raw ?? '');
  };
  const ch = d.channels;

  return (
    <>
      <p className="lede">
        Hub hlídá, že e-maily odcházejí, a při problému dá vědět mimo SmartEmailing: webhookem
        (Slack, Teams, Discord), e-mailem přes vlastní SMTP a externím hlídačem „je hub naživu“.
        Adresy, hesla a příjemci se nastavují jen v prostředí serveru (proměnné <code>ALERT_*</code>{' '}
        a <code>HEARTBEAT_URL</code>), v administraci se nikdy neukazují. Postup:{' '}
        <code>docs/DOHLED.md</code>.
      </p>

      {message ? (
        <div className={message.ok ? 'note' : 'note bad'} role="status">
          {message.text}
        </div>
      ) : null}

      <div className="tiles">
        <Tile
          label="Dohled"
          value={d.status.configured ? 'Hotový' : 'Ne'}
          bad={!d.status.configured}
          note={d.status.detail}
        />
        <Tile
          label="Aktivní upozornění"
          value={open.length}
          bad={open.some((a) => a.severity === 'critical')}
          note={
            open.length === 0
              ? 'Vše v pořádku'
              : `Z toho kritických: ${open.filter((a) => a.severity === 'critical').length}`
          }
        />
        <Tile
          label="Zálohy"
          value={
            d.probes.backup?.newestAt
              ? `${Math.max(0, Math.floor((Date.now() - new Date(d.probes.backup.newestAt).getTime()) / 3_600_000))} h`
              : d.probes.backup
                ? 'Žádná'
                : '—'
          }
          note={
            d.probes.backup
              ? `Stáří nejnovější zálohy, souborů: ${d.probes.backup.count}`
              : 'Nelze ověřit: složka se zálohami není z hubu vidět (BACKUP_DIR)'
          }
        />
        <Tile
          label="Volné místo"
          value={d.probes.disk ? `${d.probes.disk.freePercent} %` : '—'}
          note={d.probes.disk ? 'Svazek /data' : 'Nelze ověřit: MONITOR_DISK_PATH není nastaven'}
        />
      </div>

      <Panel
        title="Kanály upozornění"
        subtitle="Všechny jsou nezávislé na SmartEmailingu. Hub je ověřuje zkušebním upozorněním."
        action={
          canWrite ? (
            <button
              type="button"
              className="btn pri"
              disabled={test.isPending}
              onClick={() => {
                setMessage(null);
                test.mutate();
              }}
            >
              Poslat zkušební upozornění
            </button>
          ) : null
        }
      >
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Kanál</th>
                <th>Stav</th>
                <th>Podrobnosti</th>
                <th>Poslední použití</th>
              </tr>
            </thead>
            <tbody>
              <ChannelRow
                name="Webhook"
                configured={ch.webhook.configured}
                detail={
                  ch.webhook.format
                    ? `Formát: ${FORMAT_TEXT[ch.webhook.format] ?? ch.webhook.format}`
                    : 'Proměnná ALERT_WEBHOOK_URL'
                }
                last={ch.webhook.last}
              />
              <ChannelRow
                name="E-mail (vlastní SMTP)"
                configured={ch.email.configured}
                detail={
                  ch.email.configured
                    ? `Příjemci: ${ch.email.recipients.join(', ')}`
                    : 'Proměnné ALERT_EMAIL_TO, ALERT_EMAIL_FROM, ALERT_SMTP_*'
                }
                last={ch.email.last}
              />
              <tr>
                <th scope="row">Externí hlídač naživu</th>
                <td>
                  {ch.heartbeat.configured ? (
                    <StatusBadge tone="ok">Nastaveno</StatusBadge>
                  ) : (
                    <StatusBadge tone="bad">Nenastaveno</StatusBadge>
                  )}
                </td>
                <td>
                  {ch.heartbeat.configured
                    ? `Adresa pro selhání: ${ch.heartbeat.hasFailUrl ? 'nastavena' : 'nenastavena (chybějící signál pozná služba sama)'}`
                    : 'Proměnná HEARTBEAT_URL'}
                </td>
                <td>
                  {ch.heartbeat.last
                    ? `${dateTime(ch.heartbeat.last.at)}: ${ch.heartbeat.last.ok ? 'signál odeslán' : ch.heartbeat.last.reasons.length > 0 ? `hub nezdravý (${ch.heartbeat.last.reasons.join(', ')})` : 'odeslání selhalo'}`
                    : ''}
                </td>
              </tr>
              <tr>
                <th scope="row">Zkušební upozornění</th>
                <td>
                  {ch.testFresh ? (
                    <StatusBadge tone="ok">Prošlo</StatusBadge>
                  ) : (
                    <StatusBadge tone="warn">Neprošlo nebo staré</StatusBadge>
                  )}
                </td>
                <td>Platí 30 dnů; bez něj nejde zapnout transakční vlnu v Produkci.</td>
                <td>
                  {ch.lastTest
                    ? `${dateTime(ch.lastTest.at)}: ${ch.lastTest.ok ? 'v pořádku' : 'selhalo'}`
                    : 'Ještě neodesláno'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel
        title="Aktivní upozornění"
        subtitle="Trvají, dokud se příčina neodstraní. Kritická se připomínají."
      >
        {open.length === 0 ? (
          <EmptyState title="Žádné aktivní upozornění">
            Všechna pravidla dohledu jsou v pořádku.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table className="montable">
              <thead>
                <tr>
                  <th>Závažnost</th>
                  <th>Co se děje</th>
                  <th>Od</th>
                  <th>Odesláno</th>
                </tr>
              </thead>
              <tbody>
                {open.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <StatusBadge tone={SEVERITY[a.severity]?.tone ?? 'neu'}>
                        {SEVERITY[a.severity]?.text}
                      </StatusBadge>
                    </td>
                    <td>
                      <b>{a.title}</b>
                      <br />
                      <span className="muted">{a.detail}</span>
                    </td>
                    <td>{dateTime(a.openedAt)}</td>
                    <td>
                      {a.notified
                        ? dateTime(a.lastNotifiedAt)
                        : 'zatím ne (ztlumeno nebo kanál nefunguje)'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Uzavřená upozornění" subtitle="Posledních 30 dnů.">
        {closed.length === 0 ? (
          <EmptyState title="Nic uzavřeného">
            Za posledních 30 dnů nebylo žádné upozornění.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table className="montable">
              <thead>
                <tr>
                  <th>Závažnost</th>
                  <th>Co se dělo</th>
                  <th>Od</th>
                  <th>Do</th>
                </tr>
              </thead>
              <tbody>
                {closed.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <StatusBadge tone={SEVERITY[a.severity]?.tone ?? 'neu'}>
                        {SEVERITY[a.severity]?.text}
                      </StatusBadge>
                    </td>
                    <td>
                      <b>{a.title}</b>
                      <br />
                      <span className="muted">{a.detail}</span>
                    </td>
                    <td>{dateTime(a.openedAt)}</td>
                    <td>{dateTime(a.resolvedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="Ztlumení a okno údržby"
        subtitle="Po dobu ztlumení se upozornění jen zaznamenávají a neposílají. Nejdéle 72 hodin, zapisuje se do auditu."
      >
        {d.silences.length > 0 ? (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Pravidlo</th>
                  <th>Důvod</th>
                  <th>Do</th>
                  <th>Zapnul</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.silences.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.rule === '*'
                        ? 'Všechna pravidla'
                        : (d.rules.find((r) => r.id === s.rule)?.label ?? s.rule)}
                    </td>
                    <td>{s.reason}</td>
                    <td>{dateTime(s.until)}</td>
                    <td>{s.created_by ?? ''}</td>
                    <td>
                      {canWrite ? (
                        <button
                          type="button"
                          className="btn"
                          onClick={() => unsilence.mutate(s.id)}
                        >
                          Zrušit ztlumení
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">Nic není ztlumeno.</p>
        )}
        {canWrite ? (
          <form
            className="monform"
            onSubmit={(e) => {
              e.preventDefault();
              setMessage(null);
              silence.mutate();
            }}
          >
            <Field label="Co ztlumit">
              <select value={silRule} onChange={(e) => setSilRule(e.target.value)}>
                <option value="*">Všechna pravidla</option>
                {d.rules
                  .filter((r) => !r.external)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Na jak dlouho">
              <select value={silMinutes} onChange={(e) => setSilMinutes(Number(e.target.value))}>
                <option value={30}>30 minut</option>
                <option value={60}>1 hodinu</option>
                <option value={240}>4 hodiny</option>
                <option value={720}>12 hodin</option>
                <option value={1440}>24 hodin</option>
                <option value={4320}>72 hodin</option>
              </select>
            </Field>
            <Field label="Důvod (povinný)">
              <input
                type="text"
                value={silReason}
                maxLength={300}
                onChange={(e) => setSilReason(e.target.value)}
              />
            </Field>
            <button
              type="submit"
              className="btn"
              disabled={silReason.trim().length < 3 || silence.isPending}
            >
              Ztlumit
            </button>
          </form>
        ) : null}
      </Panel>

      <Panel title="Prahy" subtitle="Výchozí hodnoty jsou opatrné. Změna se zapisuje do auditu.">
        <form
          className="monthresholds"
          onSubmit={(e) => {
            e.preventDefault();
            setMessage(null);
            save.mutate();
          }}
        >
          {THRESHOLD_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} hint={f.unit || undefined}>
              {f.kind === 'bool' ? (
                <input
                  type="checkbox"
                  checked={Boolean(valueOf(f))}
                  disabled={!canWrite}
                  onChange={(e) => setEdit({ ...edit, [f.key]: e.target.checked })}
                />
              ) : (
                <input
                  type="number"
                  min={0}
                  value={String(valueOf(f))}
                  disabled={!canWrite}
                  onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })}
                />
              )}
            </Field>
          ))}
          {canWrite ? (
            <button
              type="submit"
              className="btn pri"
              disabled={Object.keys(edit).length === 0 || save.isPending}
            >
              Uložit prahy
            </button>
          ) : null}
        </form>
      </Panel>

      <Panel title="Co které upozornění znamená a co dělat" subtitle="Katalog pravidel dohledu.">
        <div className="monrules">
          {d.rules.map((r) => (
            <details key={r.id}>
              <summary>
                <StatusBadge tone={SEVERITY[r.severity]?.tone ?? 'neu'}>
                  {SEVERITY[r.severity]?.text ?? r.severity}
                </StatusBadge>{' '}
                {r.label}
                {r.external ? ' (hlídá externí služba)' : ''}
              </summary>
              <p>
                <b>Co to znamená.</b> {r.meaning}
              </p>
              <p>
                <b>Co dělat.</b> {r.action}
              </p>
            </details>
          ))}
        </div>
      </Panel>

      {closed.length + open.length > 0 && d.lastDigest ? (
        <p className="muted">
          Poslední denní souhrn: {dateTime(d.lastDigest.at)} (
          {d.lastDigest.ok ? 'odeslán' : 'odeslání selhalo'}).
        </p>
      ) : null}
    </>
  );
}
